import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FaLinkedin } from "react-icons/fa6";
import {
  Briefcase,
  FileText,
  Mail,
  RefreshCw,
  Send,
  Shield,
  Upload,
  User,
  X,
} from "lucide-react";
import ResumePhoneInput from "../PhoneInput/PhoneInput";
import { submitInquiry } from "../../../utils/submitInquiry";
import { getPhoneValidationError } from "../../../utils/phoneValidation";

const RecruitmentModalContext = createContext(null);

const MAX_RESUME_BYTES = 15 * 1024 * 1024;

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  linkedin: "",
  position: "",
  captcha: "",
  terms: false,
  resume: null,
};

function createCaptcha() {
  const first = Math.floor(Math.random() * 8) + 1;
  const second = Math.floor(Math.random() * 8) + 1;
  return { question: `${first} + ${second}`, answer: String(first + second) };
}

export function useRecruitmentModal() {
  const context = useContext(RecruitmentModalContext);

  if (!context) {
    throw new Error(
      "useRecruitmentModal must be used within RecruitmentModalProvider",
    );
  }

  return context;
}

function RecruitmentModal({ isOpen, position, onClose }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState(createCaptcha);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setForm({ ...EMPTY_FORM, position });
      setErrors({});
      setStatus("");
      setErrorMessage("");
      setCaptcha(createCaptcha());
    }
  }, [isOpen, position]);

  const inputClassName = (field, withIcon = false) => {
    const base =
      "h-11 w-full rounded-lg border bg-white text-sm text-left text-slate-900 outline-none transition duration-200 placeholder:text-slate-400 focus:ring-2";
    const padding = withIcon ? "pl-11 pr-4" : "px-4";
    const state = errors[field]
      ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
      : "border-[#E5E7EB] hover:border-slate-300 focus:border-[#041E42] focus:ring-[#041E42]/15";

    return `${base} ${padding} ${state}`;
  };

  const phoneInputClassName = (field) =>
    `mt-0.5 flex h-11 w-full items-center gap-2 rounded-lg border bg-white px-3 transition duration-200 focus-within:ring-2 ${
      errors[field]
        ? "border-red-500 focus-within:border-red-500 focus-within:ring-red-500/20"
        : "border-[#E5E7EB] hover:border-slate-300 focus-within:border-[#041E42] focus-within:ring-[#041E42]/15"
    }`;

  const refreshCaptcha = () => {
    setCaptcha(createCaptcha());
    setForm((current) => ({ ...current, captcha: "" }));
    setErrors((current) => ({ ...current, captcha: "" }));
  };

  const handleChange = ({ target }) => {
    const { name, type, checked, value } = target;

    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((current) => ({ ...current, [name]: "" }));
    setStatus("");
    setErrorMessage("");
  };

  const handlePhoneChange = (value) => {
    setForm((current) => ({ ...current, phone: value || "" }));
    setErrors((current) => ({ ...current, phone: "" }));
    setStatus("");
    setErrorMessage("");
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.firstName.trim())
      nextErrors.firstName = "First name is required.";
    if (!form.lastName.trim()) nextErrors.lastName = "Last name is required.";

    const phoneError = getPhoneValidationError(form.phone);
    if (phoneError) nextErrors.phone = phoneError;

    if (!form.email.trim()) nextErrors.email = "Email address is required.";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (!form.resume) nextErrors.resume = "Please upload your resume or CV.";
    else if (!/\.(pdf|doc|docx)$/i.test(form.resume.name)) {
      nextErrors.resume = "Upload a PDF, DOC, or DOCX file.";
    } else if (form.resume.size > MAX_RESUME_BYTES) {
      nextErrors.resume = "Resume must be 15 MB or smaller.";
    }

    if (!form.position.trim())
      nextErrors.position = "Position applied for is required.";
    if (!form.captcha.trim()) nextErrors.captcha = "Captcha is required.";
    else if (form.captcha.trim() !== captcha.answer)
      nextErrors.captcha = "Captcha does not match.";
    if (!form.terms)
      nextErrors.terms = "Please accept the terms and conditions.";

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) return;

    setIsSubmitting(true);
    setStatus("");
    setErrorMessage("");

    try {
      await submitInquiry("recruitment", {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        linkedin: form.linkedin.trim(),
        position: form.position.trim(),
        resume: form.resume,
      });
      setStatus("success");
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : "We could not submit your resume. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] overflow-y-auto bg-slate-950/60 p-2 backdrop-blur-sm sm:p-3"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="flex min-h-full items-center justify-center py-2">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-resume-title"
        className="flex max-h-[calc(100dvh-1rem)] w-full max-w-[700px] flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-[0_24px_64px_rgba(4,30,66,0.14)]"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 px-5 pt-3.5 pb-0 sm:px-6">
          <div className="flex min-w-0 items-start gap-2.5">
            <FileText
              size={24}
              strokeWidth={1.75}
              className="mt-0.5 shrink-0 text-[#E53935]"
              aria-hidden="true"
            />
            <div className="min-w-0">
              <h2
                id="send-resume-title"
                className="text-lg font-bold text-[#041E42] sm:text-xl"
              >
                Send Resume
              </h2>
             
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 transition duration-200 hover:bg-slate-100 hover:text-[#041E42] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#041E42]/20"
            aria-label="Close send resume form"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-2 pb-6 sm:px-6 sm:pb-6"
        >
          {(status === "success" || status === "error") && (
            <div className="mb-3 shrink-0">
              {status === "success" && (
                <p
                  role="status"
                  className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
                >
                  Thank you. Your resume submission has been sent to our recruitment
                  team.
                </p>
              )}
              {status === "error" && (
                <p
                  role="alert"
                  className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  {errorMessage ||
                    "We could not submit your resume. Please try again."}
                </p>
              )}
            </div>
          )}

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
            <div className="grid grid-cols-1 gap-x-4 gap-y-2.5 md:grid-cols-2">
              <Field
                label="First Name"
                error={errors.firstName}
                htmlFor="resume-first-name"
                required
              >
                <IconInputWrap icon={User}>
                  <input
                    id="resume-first-name"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    className={inputClassName("firstName", true)}
                    autoComplete="given-name"
                    placeholder="First name"
                  />
                </IconInputWrap>
              </Field>
              <Field
                label="Last Name"
                error={errors.lastName}
                htmlFor="resume-last-name"
                required
              >
                <IconInputWrap icon={User}>
                  <input
                    id="resume-last-name"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    className={inputClassName("lastName", true)}
                    autoComplete="family-name"
                    placeholder="Last name"
                  />
                </IconInputWrap>
              </Field>
              <Field
                label="Phone Number"
                error={errors.phone}
                htmlFor="resume-phone"
                required
              >
                <ResumePhoneInput
                  id="resume-phone"
                  name="phone"
                  value={form.phone}
                  onChange={handlePhoneChange}
                  placeholder="Phone number"
                  error={Boolean(errors.phone)}
                  className={phoneInputClassName("phone")}
                />
              </Field>
              <Field
                label="Email Address"
                error={errors.email}
                htmlFor="resume-email"
                required
              >
                <IconInputWrap icon={Mail}>
                  <input
                    id="resume-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className={inputClassName("email", true)}
                    autoComplete="email"
                    placeholder="you@email.com"
                  />
                </IconInputWrap>
              </Field>
              <Field label="LinkedIn Profile" htmlFor="resume-linkedin">
                <IconInputWrap
                  icon={FaLinkedin}
                  brandIcon
                  iconClassName="text-[#0A66C2]"
                >
                  <input
                    id="resume-linkedin"
                    name="linkedin"
                    type="url"
                    value={form.linkedin}
                    onChange={handleChange}
                    className={inputClassName("linkedin", true)}
                    placeholder="https://linkedin.com/in/your-profile"
                  />
                </IconInputWrap>
              </Field>
              <Field
                label="Upload Resume/CV"
                error={errors.resume}
                htmlFor="resume-upload"
                required
              >
                <label
                  htmlFor="resume-upload"
                  className={`mt-0.5 flex h-11 cursor-pointer items-center gap-2.5 rounded-lg border border-dashed px-2.5 transition duration-200 hover:border-sky-300 hover:bg-sky-50/40 ${
                    errors.resume
                      ? "border-red-500 bg-red-50/30"
                      : "border-[#E5E7EB] bg-white"
                  }`}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-600">
                    <Upload size={16} strokeWidth={1.75} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1 text-left">
                    {form.resume ? (
                      <>
                        <span className="block truncate text-xs font-semibold text-[#041E42] sm:text-sm">
                          {form.resume.name}
                        </span>
                        <span className="block text-[11px] text-slate-500 sm:text-xs">
                          Click to replace file
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="block text-xs font-semibold text-[#041E42] sm:text-sm">
                          Choose PDF, DOC, or DOCX
                        </span>
                        <span className="block text-[11px] text-slate-500 sm:text-xs">
                          Max file size 15 MB
                        </span>
                      </>
                    )}
                  </span>
                </label>
                <input
                  id="resume-upload"
                  name="resume"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="sr-only"
                  onChange={({ target }) => {
                    const resume = target.files?.[0] ?? null;
                    setForm((current) => ({ ...current, resume }));
                    if (resume && resume.size > MAX_RESUME_BYTES) {
                      setErrors((current) => ({
                        ...current,
                        resume: "Resume must be 15 MB or smaller.",
                      }));
                    } else {
                      setErrors((current) => ({ ...current, resume: "" }));
                    }
                    setStatus("");
                    setErrorMessage("");
                  }}
                />
              </Field>
              <Field
                label="Position Applied For"
                error={errors.position}
                htmlFor="resume-position"
                required
                className="md:col-span-2"
              >
                <IconInputWrap icon={Briefcase}>
                  <input
                    id="resume-position"
                    name="position"
                    value={form.position}
                    onChange={handleChange}
                    className={inputClassName("position", true)}
                    placeholder="e.g. Drilling Operations"
                  />
                </IconInputWrap>
              </Field>
            </div>

            <Field
              label="Captcha"
              error={errors.captcha}
              htmlFor="resume-captcha"
              required
            >
              <div
                className={`mt-0.5 flex h-11 w-full max-w-[19rem] items-stretch overflow-hidden rounded-lg border bg-white transition duration-200 sm:max-w-[20rem] ${
                  errors.captcha
                    ? "border-red-500"
                    : "border-[#E5E7EB] hover:border-slate-300"
                }`}
              >
                <div className="relative min-w-0 flex-1">
                  <Shield
                    size={16}
                    strokeWidth={1.75}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sky-500"
                    aria-hidden="true"
                  />
                  <input
                    id="resume-captcha"
                    name="captcha"
                    value={form.captcha}
                    onChange={handleChange}
                    className={`h-full w-full border-0 bg-transparent pl-9 pr-3 text-left text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-0 ${
                      errors.captcha ? "bg-red-50/40" : ""
                    }`}
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="Enter answer"
                  />
                </div>
                <span
                  className="flex w-[4.75rem] shrink-0 items-center justify-center border-l border-[#E5E7EB] bg-slate-100 px-2 text-sm font-semibold tabular-nums text-[#041E42]"
                  aria-label={`Captcha question: ${captcha.question}`}
                >
                  {captcha.question}
                </span>
                <button
                  type="button"
                  onClick={refreshCaptcha}
                  className="flex w-11 shrink-0 items-center justify-center bg-[#041E42] text-white transition duration-200 hover:bg-[#0a2f5c]"
                  aria-label="Refresh captcha"
                >
                  <RefreshCw size={16} aria-hidden="true" />
                </button>
              </div>
            </Field>
          </div>

          <div className="mt-3 shrink-0 space-y-2.5 pt-0.5">
            <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-[#E5E7EB] bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-600 sm:text-sm">
              <input
                name="terms"
                type="checkbox"
                checked={form.terms}
                onChange={handleChange}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#E5E7EB] text-[#E53935] focus:ring-[#E53935]/30"
              />
              <span>
                I agree to the{" "}
                <a
                  href="/terms-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 hover:underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  Terms &amp; Conditions
                </a>{" "}
                and my consent to SAC processing my application.
              </span>
            </label>
            {errors.terms && (
              <p className="text-sm text-red-600">{errors.terms}</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#E53935] text-sm font-bold text-white transition duration-200 hover:bg-[#d32f2f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E53935]/40 disabled:cursor-not-allowed disabled:opacity-60 sm:text-base"
            >
              <Send size={17} strokeWidth={2.25} aria-hidden="true" />
              {isSubmitting ? "Submitting…" : "Submit Resume"}
            </button>
          </div>
        </form>
      </section>
      </div>
    </div>,
    document.body,
  );
}

function IconInputWrap({
  icon: Icon,
  iconClassName = "",
  brandIcon = false,
  children,
}) {
  return (
    <div className="relative mt-0.5">
      {brandIcon ? (
        <Icon
          className={`pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 ${iconClassName}`.trim()}
          aria-hidden="true"
        />
      ) : (
        <Icon
          size={18}
          strokeWidth={1.75}
          className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sky-500 ${iconClassName}`.trim()}
          aria-hidden="true"
        />
      )}
      {children}
    </div>
  );
}

function Field({ children, error, htmlFor, label, required = false, className = "" }) {
  return (
    <div className={className}>
      <label
        htmlFor={htmlFor}
        className="text-sm font-semibold text-[#041E42]"
      >
        {label} {required && <span className="text-[#E53935]">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export function RecruitmentModalProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState("");

  const value = useMemo(
    () => ({
      openRecruitmentModal: (nextPosition = "") => {
        setPosition(nextPosition);
        setIsOpen(true);
      },
    }),
    [],
  );

  return (
    <RecruitmentModalContext.Provider value={value}>
      {children}
      <RecruitmentModal
        isOpen={isOpen}
        position={position}
        onClose={() => setIsOpen(false)}
      />
    </RecruitmentModalContext.Provider>
  );
}
